<template>
  <div class="row">
    <!-- Left Side -->
    <div class="col-12 col-md-3">
      <display-with-selection-modal :preselected-artwork="episode.artwork" @selected="(ev) => { setArtwork(ev.selected_artwork)}">
        <template #label>
          <label><strong>Artwork *</strong></label>
        </template>
      </display-with-selection-modal>
      <!-- spoof input for v-validate -->
      <input v-model="episode.artwork"
             v-validate="'required'"
             data-vv-scope="episode-general-form"
             name="Episode Artwork"
             type="text"
             class="d-none">
      <p v-show="errors.has('Episode Artwork', 'episode-general-form')" class="help text-ss-danger">
        {{ errors.first('Episode Artwork', 'episode-general-form') }}
      </p>
    </div>
    <!-- Right Side -->
    <div class="col-12 col-md-9">
      <div class="form-group">
        <label for="episode-name"><strong>{{ getItemLabel(true) }} Name * <span id="name_tooltip" class="ml-2 la la-question-circle" /></strong></label>
        <b-tooltip target="name_tooltip" triggers="hover">
          <div v-sanitise="tooltips.episode_name_tooltip" />
        </b-tooltip>
        <input id="episode-name"
               v-model="episode.name"
               v-validate="'required'"
               data-vv-scope="episode-general-form"
               name="Episode Name"
               type="text"
               class="form-control"
               aria-describedby="episodeNameHelp"
               :placeholder="`${getItemLabel(true)} Name`">
        <p v-show="errors.has('Episode Name', 'episode-general-form')" class="help text-ss-danger">
          {{ errors.first('Episode Name', 'episode-general-form') }}
        </p>
      </div>

      <div class="form-group">
        <label for="episode-description"><strong>Description *</strong></label>
        <tip-tap v-model="episode.description" input-id="episode-description" />
        <!-- hidden input for validation -->
        <input v-model="episode.description"
               v-validate="'required'"
               data-vv-scope="episode-general-form"
               name="Episode Description"
               type="text"
               class="d-none">
        <p v-show="errors.has('Episode Description', 'episode-general-form')" class="help text-ss-danger">
          {{ errors.first('Episode Description', 'episode-general-form') }}
        </p>
      </div>

      <div class="form-group">
        <label>
          <strong>Include Chapters after Description <span id="description_incl_chapters_tooltip" class="ml-2 la la-question-circle" /></strong>
        </label>
        <b-tooltip target="description_incl_chapters_tooltip" triggers="hover">
          <div v-sanitise="tooltips.description_incl_chapters" />
        </b-tooltip>
        <div>
          <span class="kt-switch kt-switch--icon">
            <label>
              <input v-model="episode.description_incl_chapters"
                     type="checkbox"
                     :checked="episode.description_incl_chapters"
                     name="Include Chapters after Description">
              <span />
            </label>
          </span>
        </div>
      </div>

      <div class="form-row mb-4">
        <div class="col-auto mr-4 mb-4">
          <label><strong>Explicit</strong></label>
          <radio-group v-model="episode.episode_detail.explicit"
                       group-name="episode_explicitly"
                       :options="episode_explicit_options" />
        </div>

        <div class="col-auto mb-4">
          <label><strong>Type <span id="type_tooltip" class="ml-2 la la-question-circle" /></strong></label>
          <b-tooltip target="type_tooltip" triggers="hover">
            <div v-sanitise="tooltips.episode_type_tooltip" />
          </b-tooltip>
          <radio-group v-model="episode.episode_detail.type"
                       group-name="episode_type"
                       :options="episode_type_options" />
        </div>
      </div>
      <div class="form-row mb-4">
        <div class="col-6">
          <label><strong>Season No. {{ season_is_required ? '*' : '' }}<span id="season_no_tooltip" class="ml-2 la la-question-circle" /></strong></label>
          <b-tooltip target="season_no_tooltip" triggers="hover">
            <div v-sanitise="tooltips.season_no_tooltip" />
          </b-tooltip>
          <input v-model="episode.episode_detail.season_number"
                 v-validate="season_rules"
                 data-vv-scope="episode-general-form"
                 name="Season Number"
                 type="number"
                 min="0"
                 class="form-control">
          <p v-show="errors.has('Season Number', 'episode-general-form')" class="help text-ss-danger">
            {{ errors.first('Season Number', 'episode-general-form') }}
          </p>
        </div>
        <div class="col-6">
          <label><strong>Episode No. {{ episode_is_required ? '*' : '' }}<span id="episode_no_tooltip" class="ml-2 la la-question-circle" /></strong></label>
          <b-tooltip target="episode_no_tooltip" triggers="hover">
            <div v-sanitise="tooltips.episode_no_tooltip" />
          </b-tooltip>
          <input v-model="episode.episode_detail.episode_number"
                 v-validate="episode_rules"
                 data-vv-scope="episode-general-form"
                 name="Episode Number"
                 type="number"
                 min="0"
                 class="form-control">
          <p v-show="errors.has('Episode Number', 'episode-general-form')" class="help text-ss-danger">
            {{ errors.first('Episode Number', 'episode-general-form') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
<script>

import { mapWritableState, mapActions } from 'pinia';
import { useEpisodeManageStore } from "VueStore/app/episodeManage";
import DisplayWithSelectionModal from "VuePartials/Artwork/DisplayWithSelectionModal";
import RadioGroup from "VueComponents/RadioGroup";
import PodcastValidateMixin from "VueMixins/PodcastValidateMixin";
import VeeValidate from 'vee-validate';
import TooltipsMixin from "VueMixins/TooltipsMixin";
VeeValidate.config.useConstraintAttrs = false;

export default {
  components: {
    DisplayWithSelectionModal,
    RadioGroup,
  },
  mixins: [PodcastValidateMixin, TooltipsMixin],
  computed: {
    ...mapWritableState(useEpisodeManageStore, ['episode']),
    ...mapWritableState(useEpisodeManageStore, ['podcast']),
    ...mapWritableState(useEpisodeManageStore, ['episode_type_options']),
    ...mapWritableState(useEpisodeManageStore, ['episode_explicit_options']),
    season_is_required() {
      // If podcast type is serial and episode type is full
      const is_serial = this.podcast?.itunes_detail?.type === 'serial';
      const is_full_episode = this.episode.episode_detail?.type === 'full';
      return is_serial && is_full_episode;
    },
    episode_is_required() {
      const is_serial = this.podcast?.itunes_detail?.type === 'serial';
      const is_full_episode = this.episode.episode_detail?.type === 'full';
      return is_serial && is_full_episode;
    },
    season_rules() {
      return this.season_is_required ? 'required|integer' : 'integer';
    },
    episode_rules() {
      return this.episode_is_required ? 'required|integer' : 'integer';
    },
  },
  methods: {
    ...mapActions(useEpisodeManageStore, ['setArtwork']),
  },
};
</script>
